/*
 * GetDiscountPreview
 *
 * Fetches the information about a discount, including the priceRules section.
 * This is for showing a "preview" of what the discount amount will be (before
 * we know what SKU it will be applied to)
 */
import { useQuery } from '@tanstack/react-query';

import useCurrency from '@/app/hooks/useCurrency';
import { graphql, type ResultOf, type VariablesOf } from '@/graphql';
import gqlClient from '@/utils/gqlClient';

const query = graphql(`
  query GetDiscountPreview($discountCode: String!, $currencyCode: String) {
    content(currency: $currencyCode) {
      discount(discountCode: $discountCode) {
        priceRules {
          amount {
            amount
            currencyCode
          }
        }
      }
    }
  }
`);

export type GetDiscountPreviewResult = ResultOf<typeof query>;
export type GetDiscountPreviewVariables = VariablesOf<typeof query>;

export function withQueryKey(vars: GetDiscountPreviewVariables) {
  return {
    queryKey: ['GetDiscountPreview', vars.discountCode, vars.currencyCode],
    queryFn: async () => {
      if (!vars.discountCode) return null;

      const data = await gqlClient().request(query, vars);

      return data.content?.discount;
    },
  };
}

export function useDiscountPreview(discountCode = '') {
  const { currencyCode } = useCurrency();
  const variables: GetDiscountPreviewVariables = {
    discountCode,
    currencyCode,
  };

  return useQuery({
    ...withQueryKey({
      ...variables,
    }),
  });
}
